import { Fragment, useState } from 'react';
import { Button } from '@rudi/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { useAgentToolLatency } from '../api/hooks';
import type { AgentToolLatency } from '../api/types';

/** Rudi keeps the tool call ledger and reads up to 90 days of it. */
const WINDOW_OPTIONS = [1, 7, 30, 90] as const;
const DEFAULT_WINDOW_DAYS = 7;
const TOOL_COLUMNS = 7;

function milliseconds(value: number | null): string {
  return value === null ? 'No data' : `${Math.round(value).toLocaleString()} ms`;
}

function days(count: number): string {
  return count === 1 ? '1 day' : `${count} days`;
}

function Errors({ tool }: { tool: AgentToolLatency }) {
  if (tool.errorCount === 0) return <>0</>;
  return (
    <div>
      <div>{tool.errorCount.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground">
        {tool.errors.map(error => `${error.code} ${error.count.toLocaleString()}`).join(', ')}
      </div>
    </div>
  );
}

function Operations({ tool }: { tool: AgentToolLatency }) {
  return (
    <Table aria-label={`${tool.tool} operations`}>
      <TableHeader>
        <TableRow>
          <TableHead>Operation</TableHead>
          <TableHead>Calls</TableHead>
          <TableHead>p50</TableHead>
          <TableHead>p95</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tool.operations.map(operation => (
          <TableRow key={operation.operation}>
            <TableCell className="font-mono text-xs">{operation.operation}</TableCell>
            <TableCell>{operation.callCount.toLocaleString()}</TableCell>
            <TableCell>{milliseconds(operation.p50Ms)}</TableCell>
            <TableCell>{milliseconds(operation.p95Ms)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** How long Rudi's agents waited on each MCP tool, from Rudi's tool call ledger. */
export function AgentToolLatencySection() {
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS);
  const { data, error, isLoading } = useAgentToolLatency(windowDays);
  const [expandedTool, setExpandedTool] = useState<string | null>(null);

  return (
    <section className="rounded-3xl border bg-card p-5" aria-label="Agent tool calls">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-semibold">Agent tool calls</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            How long Rudi&apos;s MCP tools took for agents,{' '}
            {data?.scope === 'tenant' ? 'in your tenant' : 'pooled from every tenant'}. Percentiles
            cover completed and failed calls.
          </p>
        </div>
        <label className="space-y-1 text-sm" htmlFor="agent-tools-days">
          <span className="font-medium">Tool call window</span>
          <select
            id="agent-tools-days"
            className="h-10 w-full rounded-3xl border border-input bg-background px-3"
            value={windowDays}
            onChange={event => setWindowDays(Number(event.target.value))}
          >
            {WINDOW_OPTIONS.map(count => (
              <option key={count} value={count}>
                Last {days(count)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <p className="mt-4 text-sm text-muted-foreground">Loading agent tool calls…</p>}
      {error && !isLoading && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          Could not load agent tool calls:{' '}
          {error instanceof Error ? error.message : 'the performance API returned an error.'}
        </p>
      )}
      {data === null && (
        <p className="mt-4 text-sm text-muted-foreground">
          Agent tool latency is not available on this Rudi yet.
        </p>
      )}
      {data && data.tools.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">
          No agent tool calls in the last {days(data.windowDays)}.
        </p>
      )}
      {data && data.tools.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <Table aria-label="Agent tool latency">
            <TableHeader>
              <TableRow>
                <TableHead>Tool</TableHead>
                <TableHead>Calls</TableHead>
                <TableHead>Errors</TableHead>
                <TableHead>p50</TableHead>
                <TableHead>p95</TableHead>
                <TableHead>Max</TableHead>
                <TableHead className="text-right">Operations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.tools.map(tool => {
                const expanded = expandedTool === tool.tool;
                return (
                  <Fragment key={tool.tool}>
                    <TableRow>
                      <TableCell className="font-mono text-xs">{tool.tool}</TableCell>
                      <TableCell>{tool.callCount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Errors tool={tool} />
                      </TableCell>
                      <TableCell>{milliseconds(tool.p50Ms)}</TableCell>
                      <TableCell>{milliseconds(tool.p95Ms)}</TableCell>
                      <TableCell>{milliseconds(tool.maxMs)}</TableCell>
                      <TableCell className="text-right">
                        {tool.operations.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            aria-expanded={expanded}
                            onClick={() => setExpandedTool(expanded ? null : tool.tool)}
                          >
                            {expanded ? 'Hide' : 'View'} {tool.tool} operations
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow>
                        <TableCell colSpan={TOOL_COLUMNS} className="bg-muted/30 p-5">
                          <Operations tool={tool} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
